import { DeleteOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Space, Typography } from 'antd';
import { Investor, Investment } from 'procura-shared';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';

type DeleteButtonProps = {
  investor?: Investor;
  investment?: Investment;
  type: 'Investor' | 'Investment';
  style?: React.CSSProperties;
};

export const DeleteButton = ({ investor, investment, type, style }: DeleteButtonProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<{ name: string }>({ name: '' });
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const canRender = (): boolean => (type === 'Investor' ? investor != null : investment != null);
  const investorLabel = (): string => investor.name ?? investor.contactEmail ?? investor.id;
  const investmentLabel = (): string => investment.name ?? investment.id;
  const label = (): string => (type === 'Investor' ? investorLabel() : investmentLabel());
  const canDelete = () => label() === values.name;

  const formChangeHandler = (changedValues) => setValues({ ...values, ...changedValues });

  const buttonClickHandler = () => setVisible(true);
  const modalCancelHandler = () => setVisible(false);

  const confirmHandler = async () => {
    setLoading(true);
    try {
      if (type === 'Investment') {
        await calls.investments.deleteInvestment(investment.id);
      } else if (type === 'Investor') {
        await calls.investors.deleteInvestor(investor.id);
      }
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const renderModalButtons = () => [
    <Button key="cancel" onClick={modalCancelHandler} disabled={loading}>
      {t('cancel')}
    </Button>,
    <Button
      key="delete"
      onClick={confirmHandler}
      danger
      type="primary"
      loading={loading}
      disabled={!canDelete()}
      icon={<DeleteOutlined />}>
      {t('delete')}
    </Button>,
  ];

  return (
    canRender() && (
      <>
        <Modal title={t('deleteConfirm')} visible={visible} closable={false} footer={renderModalButtons()}>
          <Space direction="vertical" size={12}>
            <Typography.Text strong type="danger">
              {t('deleteConfirmDescription')}
            </Typography.Text>
            <Typography.Text>
              {t('deleteConfirmInput')} <Typography.Text code>{label()}</Typography.Text>
            </Typography.Text>
            <Form onValuesChange={formChangeHandler}>
              <Form.Item name="name" rules={[{ required: true, message: t('textInvalid'), type: 'string' }]}>
                <Input value={values.name} type="name" placeholder={label()} readOnly={loading} />
              </Form.Item>
            </Form>
          </Space>
        </Modal>
        <Button danger type="default" style={style} onClick={buttonClickHandler}>
          Delete {label()}
        </Button>
      </>
    )
  );
};
